import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useStableCallback from '~/hooks/common/useStableCallback';

import type { TypeTableSelection } from './table.types';

export type TypeSelectionHeaderState = 'all' | 'some' | 'none';

/** 메서드 표기로 두어, 행 타입이 다른 표도 같은 context(TypeTableSelectionApi<unknown>)로 넘길 수 있다. */
export type TypeTableSelectionApi<TData> = {
  mode: 'multiple' | 'single';
  showDisabledCheckboxes: boolean;
  dimUnselectableRows: boolean;
  isSelected(row: TData, index: number): boolean;
  isSelectable(row: TData): boolean;
  toggleRow(row: TData, index: number): void;
  toggleAll(): void;
  headerState: TypeSelectionHeaderState;
};

type TypeUseTableSelectionParams<TData> = {
  rows: TData[];
  getRowId?: (row: TData, index: number) => string;
  selection?: TypeTableSelection<TData>;
};

/**
 * 셀 안 체크박스 선택 상태. 선택은 행 id(getRowId, 없으면 행 순서)로 보관한다.
 *
 * - getRowId 가 없으면 데이터가 바뀔 때 선택을 비운다. 행 순서가 id 라 새 데이터에서는 다른 행을 가리키기 때문이다.
 * - getRowId 가 있으면 새 데이터에 남아 있는 행만 선택을 유지한다.
 */
const useTableSelection = <TData>({
  rows,
  getRowId,
  selection,
}: TypeUseTableSelectionParams<TData>): TypeTableSelectionApi<TData> | null => {
  const [innerIds, setInnerIds] = useState<string[]>([]);
  const isControlled = selection?.selectedRowIds !== undefined;
  const selectedIds = useMemo(
    () => new Set(isControlled ? selection?.selectedRowIds : innerIds),
    [isControlled, selection?.selectedRowIds, innerIds]
  );

  const notifyChange = useStableCallback((selectedRows: TData[]) => selection?.onChange?.(selectedRows));

  const idOf = useCallback(
    (row: TData, index: number) => (getRowId ? getRowId(row, index) : String(index)),
    [getRowId]
  );
  const isRowSelectable = selection?.isRowSelectable;
  const isSelectable = useCallback((row: TData) => isRowSelectable?.(row) ?? true, [isRowSelectable]);

  const commit = useCallback(
    (ids: Set<string>) => {
      if (!isControlled) setInnerIds(Array.from(ids));
      notifyChange(rows.filter((row, index) => ids.has(idOf(row, index))));
    },
    [isControlled, rows, idOf, notifyChange]
  );

  const prevRowsRef = useRef(rows);
  useEffect(() => {
    if (prevRowsRef.current === rows) return;
    prevRowsRef.current = rows;
    if (isControlled || innerIds.length === 0) return;

    const remaining = getRowId ? new Set(rows.map(idOf).filter((id) => selectedIds.has(id))) : new Set<string>();
    if (remaining.size !== innerIds.length) commit(remaining);
  }, [rows, isControlled, innerIds.length, getRowId, idOf, selectedIds, commit]);

  const mode = selection?.mode ?? 'multiple';

  const toggleRow = useCallback(
    (row: TData, index: number) => {
      if (!isSelectable(row)) return;
      const id = idOf(row, index);
      const next = new Set(mode === 'single' ? [] : selectedIds);
      if (selectedIds.has(id)) next.delete(id);
      else next.add(id);
      commit(next);
    },
    [isSelectable, idOf, mode, selectedIds, commit]
  );

  const selectableIds = useMemo(
    () => rows.flatMap((row, index) => (isSelectable(row) ? [idOf(row, index)] : [])),
    [rows, isSelectable, idOf]
  );
  const selectedSelectableCount = selectableIds.filter((id) => selectedIds.has(id)).length;
  const headerState: TypeSelectionHeaderState =
    selectedSelectableCount === 0 ? 'none' : selectedSelectableCount === selectableIds.length ? 'all' : 'some';

  const toggleAll = useCallback(() => {
    commit(headerState === 'all' ? new Set() : new Set(selectableIds));
  }, [commit, headerState, selectableIds]);

  const isSelected = useCallback((row: TData, index: number) => selectedIds.has(idOf(row, index)), [selectedIds, idOf]);

  const hasSelection = selection !== undefined;
  const showDisabledCheckboxes = selection?.showDisabledCheckboxes ?? false;
  const dimUnselectableRows = selection?.dimUnselectableRows ?? false;

  // context 값이라, 매 렌더 새 객체면 선택 칸이 행 수만큼 다시 렌더된다.
  return useMemo(
    () =>
      hasSelection
        ? {
            mode,
            showDisabledCheckboxes,
            dimUnselectableRows,
            isSelected,
            isSelectable,
            toggleRow,
            toggleAll,
            headerState,
          }
        : null,
    [
      hasSelection,
      mode,
      showDisabledCheckboxes,
      dimUnselectableRows,
      isSelected,
      isSelectable,
      toggleRow,
      toggleAll,
      headerState,
    ]
  );
};

export default useTableSelection;
